import { formatDate } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { concat, map, Observable, of, switchMap } from 'rxjs';
import { DefaultEnvironment, ENVIRONMENT_TOKEN } from '@trovata/app/core/models/config.model';
import { AgentActivityWindowService } from '@trovata/app/features/trovata-ai/suggestions/services/agent-activity-window.service';
import { CashAgentRunsSource, CashRunWindow } from '@trovata/app/features/trovata-ai/suggestions/services/cash-agent-runs.source';
import { mergeAgentCard, mergeDigestAgentMention } from '@trovata/app/features/trovata-ai/suggestions/utils/merge-agent-card';
import { SuggestionCard, TMS_AGENT_ACTIVITY_RULE_ID } from '@trovata/app/features/trovata-ai/suggestions/models/suggestion-card.model';
import { SuggestionFeedResult, SuggestionsProvider } from '@trovata/app/features/trovata-ai/suggestions/services/suggestions-provider';
import { parseDigest, parseSuggestionCards, parseWorkEvents, ttlSecondsFrom } from '@trovata/app/features/trovata-ai/suggestions/utils/parse-suggestions';
import { SuggestionContext, WorkEvent } from '@trovata/app/features/trovata-ai/suggestions/models/suggestion-card.model';

/**
 * TmsSuggestionsProvider — the TMS implementation of {@link SuggestionsProvider}.
 *
 * GETs the ATOM `AppDataServlet` `?action=home-suggestions` action and parses
 * the versioned envelope into clean cards (tolerating unknown fields / unknown
 * action kinds — {@link parseSuggestionCards}).
 *
 * Auth: hits the `/api/v1/app-data` route, which passes through ATOM's
 * `RestAuthFilter` (Bearer-authenticated). `AuthInterceptor` attaches the Bearer
 * to `/api/*` URLs automatically — NO hand-rolled auth headers (Jackson rule #2).
 * Mirrors the base-URL construction of `TmsAgentsDataProvider`.
 *
 * Cash-isolation: only ever resolved by `SuggestionsProviderRouter` for a
 * non-Cash context; the Cash path never reaches it.
 */
@Injectable({ providedIn: 'root' })
export class TmsSuggestionsProvider implements SuggestionsProvider {
	private static readonly ACTION_HOME_SUGGESTIONS: string = 'home-suggestions';
	private static readonly ACTION_WORK_GRAPH: string = 'work-graph';
	/** R5's window param — minutes back from the SERVER's clock (TZ-proof). One window authority
	 *  (§23): the server computes the agent card, its digest mention, and evidence.runs over this
	 *  value, so the card and the "Worth a look" line can never disagree. Omitted when the
	 *  selected range cannot be derived (last-seen with no stamp) — the server then applies its
	 *  own 24h default, which matches the client pill's default. */
	private static readonly PARAM_AGENT_SINCE_MINUTES: string = 'agentSinceMinutes';
	/** Server cap (WORK_GRAPH_MAX_LIMIT) — the multi-day history (§45) wants the full window. */
	private static readonly WORK_GRAPH_LIMIT: string = '50';
	/** Activity-history window (§45): 7 days of the user's own actions, today first. */
	private static readonly WORK_GRAPH_WINDOW_DAYS: number = 7;
	/** The server's strict `since` format (normalizeSince) — ISO seconds, MACHINE axis (bounds AUDIT_DATE, §40). */
	// eslint-disable-next-line @stylistic/quotes -- prettier's avoidEscape form
	private static readonly SINCE_FORMAT: string = "yyyy-MM-dd'T'HH:mm:ss";

	private readonly baseUrl: string;
	/** The Cash half of the merged agents card (design §9): fetched alongside the ATOM feed, merged
	 *  in THIS provider — the state and every component see one card and never know two sources
	 *  exist. Failure-isolated: a Cash error degrades only the Cash half. */
	private readonly cashRuns: CashAgentRunsSource = inject(CashAgentRunsSource);

	constructor(
		private readonly http: HttpClient,
		private readonly activityWindow: AgentActivityWindowService,
		@Inject(ENVIRONMENT_TOKEN) private readonly environment: DefaultEnvironment
	) {
		this.baseUrl = `${this.environment.atomApiUrl}/api/v1/app-data`;
	}

	getSuggestions(context: SuggestionContext): Observable<SuggestionFeedResult> {
		let params: HttpParams = new HttpParams().set('action', TmsSuggestionsProvider.ACTION_HOME_SUGGESTIONS);
		// Read at fetch time so a range switch (which forces a re-fetch) lands here naturally.
		// This provider only runs for a TMS-entitled context, so touching the window service —
		// which arms its last-seen tracking — is a TMS-surface act, never a Cash one. Null until
		// the user has actively chosen a range (see wireSinceMinutes) — the server's default and
		// its quiet-day no-card rule apply until then.
		const windowMinutes: number | null = this.activityWindow.wireSinceMinutes();
		if (windowMinutes !== null) {
			params = params.set(TmsSuggestionsProvider.PARAM_AGENT_SINCE_MINUTES, String(windowMinutes));
		}
		// TWO-PHASE emission (§6.5 — nothing waits on the slowest source): the ATOM feed paints the
		// moment it lands (TMS agent card as-is), then the SAME result re-emits with the Cash half
		// merged in when the workspace API answers (observed ~25s over a VPN — a forkJoin here held
		// the whole briefing blank for exactly that long). The state applies each emission and marks
		// the feed SETTLED only on completion, so the AI brief still generates exactly once, from
		// the final fact-set.
		return this.http.get<unknown>(this.baseUrl, { params, withCredentials: true }).pipe(
			switchMap((feed: unknown): Observable<SuggestionFeedResult> => {
				const tmsOnly: SuggestionFeedResult = TmsSuggestionsProvider.compose(feed, undefined);
				// ONE window authority (CR-JAVA-012 / CR-NG-026): the Cash half counts over the
				// EFFECTIVE window ATOM echoed back on the agent card (inputs.sinceMinutes — the
				// value after ATOM's default and ceiling), so the two halves can never fork if the
				// server default ever moves. The local fallback covers only a feed with no agent
				// card to echo from (quiet TMS day).
				const cashWindowMinutes: number = TmsSuggestionsProvider.echoedWindowMinutes(tmsOnly) ?? windowMinutes ?? TmsSuggestionsProvider.DEFAULT_WINDOW_MINUTES;
				return concat(
					of(tmsOnly),
					this.cashRuns
						.fetchWindow(cashWindowMinutes)
						.pipe(map((cash: CashRunWindow | null): SuggestionFeedResult => TmsSuggestionsProvider.compose(feed, cash)))
				);
			})
		);
	}

	/** The ATOM-effective agent window echoed on the card, or null when no card carries one. */
	private static echoedWindowMinutes(result: SuggestionFeedResult): number | null {
		const card: SuggestionCard | undefined = result.cards.find((candidate: SuggestionCard): boolean => candidate.reason.ruleId === TMS_AGENT_ACTIVITY_RULE_ID);
		const echoed: unknown = card?.reason.inputs['sinceMinutes'];
		return typeof echoed === 'number' && echoed > 0 ? echoed : null;
	}

	/**
	 * The ONE composition of a feed result from the ATOM envelope + the Cash window.
	 * `cash === undefined` = still in flight (phase 1: the TMS cards verbatim, no degraded flag);
	 * `cash === null` = fetch failed (degraded flag on); a window = the full merge.
	 */
	private static compose(feed: unknown, cash: CashRunWindow | null | undefined): SuggestionFeedResult {
		const cards: SuggestionCard[] = parseSuggestionCards(feed);
		const digest: ReturnType<typeof parseDigest> = parseDigest(feed);
		const ttlSeconds: number = ttlSecondsFrom(feed);
		if (cash === undefined) {
			return { cards, digest, ttlSeconds };
		}
		const tmsAgentIndex: number = cards.findIndex((card: SuggestionCard): boolean => card.reason.ruleId === TMS_AGENT_ACTIVITY_RULE_ID);
		const tmsAgentCard: SuggestionCard | null = tmsAgentIndex >= 0 ? cards[tmsAgentIndex] : null;
		const merged: SuggestionCard | null = mergeAgentCard(tmsAgentCard, cash, Date.now());
		if (tmsAgentIndex >= 0) {
			if (merged !== null) {
				cards.splice(tmsAgentIndex, 1, merged);
			} else {
				cards.splice(tmsAgentIndex, 1);
			}
		} else if (merged !== null) {
			cards.push(merged);
		}
		return {
			cards,
			// The Worth-a-look mention must match the MERGED card (the "20 vs 0" rule).
			digest: [...mergeDigestAgentMention(digest, tmsAgentCard?.title ?? null, merged)],
			ttlSeconds,
		};
	}

	/** Matches the server rule's own default window — one 24h default, no handshake. */
	private static readonly DEFAULT_WINDOW_MINUTES: number = 1440;

	/** The user's own events from the work-graph read (§29.4), over the §45 multi-day
	 *  history window (midnight-anchored so the oldest day is complete, not clipped). */
	getWorkEvents(): Observable<WorkEvent[]> {
		const since: Date = new Date();
		since.setDate(since.getDate() - (TmsSuggestionsProvider.WORK_GRAPH_WINDOW_DAYS - 1));
		since.setHours(0, 0, 0, 0);
		const params: HttpParams = new HttpParams()
			.set('action', TmsSuggestionsProvider.ACTION_WORK_GRAPH)
			.set('limit', TmsSuggestionsProvider.WORK_GRAPH_LIMIT)
			.set('since', formatDate(since, TmsSuggestionsProvider.SINCE_FORMAT, 'en-US'));
		return this.http.get<unknown>(this.baseUrl, { params, withCredentials: true }).pipe(map((response: unknown): WorkEvent[] => parseWorkEvents(response)));
	}
}
